import { QR_CUPO_PLAN, QR_LIST } from "@/apollo/queries/qr";
import { apolloClient } from "@/vue-apollo";
import { exQue } from "@/utils/gql";

// Cupo de códigos QR del plan de la empresa: cuántos incluye (usuarios_plan.cantidad_qr de
// DashboardEmpresaYear, que suma plan y adicionales) y cuántos hay creados.
//
// Mientras el backend no publique cantidad_qr (ver claude_docs/Planes/ en el workspace), la
// consulta falla porque GraphQL no conoce el campo. En ese caso, y también si llega null o algo
// que no es un número, el límite queda desconocido y el módulo funciona sin tope, como antes:
// el límite se activa solo cuando el backend lo publique. El freno de verdad tiene que estar en
// qrAdminSave; este es para que la persona no complete el formulario en vano.
//
// Cuentan todos los QR de la empresa, también los borradores y los archivados: archivar no
// detiene el enlace, y un borrador se publica sin volver a pasar por aquí.
//
// El estado es una función por la misma razón que en Carpetas.js.
export const CupoState = () => ({
  // undefined: todavía no se consultó en esta sesión. null: el plan no trae un límite.
  limiteQr: undefined,
  usadosQr: null,
});

export const CupoMutations = {
  setLimiteQr(state, limite) {
    state.limiteQr = limite;
  },
  setUsadosQr(state, usados) {
    state.usadosQr = usados;
  },
};

export const CupoGetters = {
  // { limite, usados, alcanzado }. Sin límite conocido, o sin poder contar los QR, nunca está
  // alcanzado: ante la duda no se bloquea.
  cupoQr: (state) => {
    const limite = state.limiteQr === undefined ? null : state.limiteQr;
    const usados = state.usadosQr;
    return {
      limite,
      usados,
      alcanzado: limite !== null && usados !== null && usados >= limite,
    };
  },
};

// Límite del plan, o null si no se pudo leer. Va sin caché de Apollo: es la misma consulta
// del Inicio, y mezclar en la caché sus campos con los de acá haría que se pisen.
async function leerLimite(fecha) {
  try {
    const { data } = await apolloClient.query({
      query: QR_CUPO_PLAN,
      variables: { fecha },
      fetchPolicy: "no-cache",
    });
    const valor = data?.DashboardEmpresaYear?.usuarios_plan?.cantidad_qr;
    if (valor === null || valor === undefined || valor === "") return null;
    const limite = Number(valor);
    return Number.isInteger(limite) && limite >= 0 ? limite : null;
  } catch (error) {
    // Es lo esperado mientras el backend no tenga el campo: sin límite y sin avisar
    console.warn("El plan no informa el límite de códigos QR:", error.message);
    return null;
  }
}

export const CupoActions = {
  // Revisa el cupo. El límite se pide una vez por sesión, porque DashboardEmpresaYear también
  // calcula las estadísticas del Inicio; los QR usados se cuentan cada vez, porque otra persona
  // pudo crear uno. `fecha` es la de hoy en AAAA-MM-DD: la consulta la exige, aunque el plan no
  // depende de ella. Nunca lanza. Devuelve el getter cupoQr.
  async revisarCupoQr({ state, commit, getters }, { fecha }) {
    if (state.limiteQr === undefined) commit("setLimiteQr", await leerLimite(fecha));

    let usados = null;
    // Sin límite no hace falta contar
    if (state.limiteQr !== null) {
      try {
        const pagina = await exQue(
          QR_LIST,
          { search: "", offset: 0, limit: 1, folderId: "", archive: "all" },
          true,
        );
        const total = Number(pagina?.total);
        usados = Number.isInteger(total) ? total : null;
      } catch (_) {
        // exQue ya lo dejó en consola; sin el conteo no se bloquea
      }
    }
    commit("setUsadosQr", usados);
    return getters.cupoQr;
  },
};
